import {
  buildComparisonTimestampAccessor,
  buildMeasureAccessor,
  buildTimestampAccessor,
  DataRow,
  DataTable,
  dateFormatterFactory,
  DateTrunc,
  FiscalYearStartMonth,
  generateDataTableWithComparison,
  getConstantEffectiveDefinition,
  maxPrecision,
  parseDataPoints,
  validDateOrUndefined
} from '@tableau/metrics-engine';
import { MetricCardWithTimeSeriesProps, defaultComparisonFor } from '@tableau/metrics-shared-widgets-dom/';
import { MetricDetailViewProps } from '@tableau/metrics-shared-widgets-dom/lib/src/MetricDetailView';
import { IMetricDataInfo, IMetricInfo } from './models/IMetricInfo';
import { DateGranularity, IMetricDataList, IMetricDataPointsSchema } from '../ts/ServerApi/types/IMetricDataList';
import { FeatureFlags } from '../ts/FeatureFlags';

export const metricCardProps = (metric: IMetricDataInfo, metricInfo?: IMetricInfo): MetricCardWithTimeSeriesProps => {
  const data = metric.dataPoints ? metric.dataPoints : [];
  const measureFormat = metric.schema ? metric.schema.measure.format : '';
  const dateTrunc = dateTruncFromSchema(metricInfo.schema);
  const comparison = defaultComparisonFor(dateTrunc);
  const dataWithComparison = generateDataTableWithComparison(data, data.slice(-100), comparison);
  const timestampAccessor = buildTimestampAccessor(getConstantEffectiveDefinition());
  const measureAccessor = buildMeasureAccessor(getConstantEffectiveDefinition());

  return {
    testId: 'metric-card',
    data: dataWithComparison,
    timestampAccessor,
    measureAccessor,
    lastUpdatedTimestamp: validDateOrUndefined(metric.dataRefreshedTimestamp),
    measureFormat,
    suspended: false,
    comparison,
    metricColorIndicatorsEnabled: FeatureFlags.metricColorIndicatorsEnabled,
  };
};

export const getTableWithDefaultComparison = (metric: IMetricDataInfo): DataTable => {
  const data = metric.dataPoints ? metric.dataPoints : [];
  const dateTrunc = dateTruncFromSchema(metric.schema);
  const comparison = defaultComparisonFor(dateTrunc);
  return generateDataTableWithComparison(data, data.slice(-100), comparison);
};

export const getMergedMetricInfo = (metricInfo: IMetricInfo, metricDataList: IMetricDataList): IMetricInfo => {
  const data = metricDataList.data[metricInfo.id];
  if (data) {
    return { ...metricInfo,
      dataPoints: parseDataPoints(data.points, maxPrecision(data.schema?.measure?.format)),
      schema: data.schema,
    dataRefreshedTimestamp: data.dataRefreshedTimestamp };
  } else {
    return metricInfo;
  }
};

export const getMetricMeasure = (metric: IMetricDataInfo, measureAccessor: (point: DataRow) => number): number => {
  return metric && metric.dataPoints && metric.dataPoints.length > 0 && measureAccessor(metric.dataPoints[metric.dataPoints.length - 1]);
};

export const getMeasureFormat = (metric: IMetricDataInfo): string | undefined => {
  return metric && metric.schema && metric.schema.measure.format;
};

export const metricDetailProps = (metric: IMetricInfo): MetricDetailViewProps => {
  const data = metric.dataPoints ? metric.dataPoints : [];
  const dateTrunc = dateTruncFromSchema(metric.schema);
  const fiscalYearStartMonth = metric.schema?.timestamp?.fiscalYearStartMonth || FiscalYearStartMonth.JANUARY;
  const dateFormatter = dateFormatterFactory(dateTrunc, fiscalYearStartMonth);
  const comparison = defaultComparisonFor(dateTrunc);
  const timestampAccessor = buildTimestampAccessor(getConstantEffectiveDefinition());
  const measureAccessor = buildMeasureAccessor(getConstantEffectiveDefinition());
  const comparisonTimestampAccessor = buildComparisonTimestampAccessor(getConstantEffectiveDefinition());

  return {
    metricName: metric.name,
    testId: 'metric-detail',
    data,
    timestampAccessor,
    measureAccessor,
    comparisonTimestampAccessor,
    dateFormatter,
    lastUpdatedTimestamp: validDateOrUndefined(metric.dataRefreshedTimestamp),
    measureFormat: metric.schema ? metric.schema.measure.format : '',
    comparison
  };
};

export const dateTruncFromGranularity = (date?: DateGranularity): DateTrunc => {
  switch (date) {
    case DateGranularity.SECOND: {
      return DateTrunc.SECOND;
    }
    case DateGranularity.MINUTE: {
      return DateTrunc.MINUTE;
    }
    case DateGranularity.HOUR: {
      return DateTrunc.HOUR;
    }
    case DateGranularity.DAY: {
      return DateTrunc.DAY;
    }
    case DateGranularity.ISO_WEEKDAY: {
      return DateTrunc.ISO_WEEKDAY;
    }
    case DateGranularity.WEEK: {
      return DateTrunc.WEEK;
    }
    case DateGranularity.ISO_WEEK: {
      return DateTrunc.ISO_WEEK;
    }
    case DateGranularity.MONTH: {
      return DateTrunc.MONTH;
    }
    case DateGranularity.QUARTER: {
      return DateTrunc.QUARTER;
    }
    case DateGranularity.ISO_QUARTER: {
      return DateTrunc.ISO_QTR;
    }
    case DateGranularity.YEAR: {
      return DateTrunc.YEAR;
    }
    case DateGranularity.ISO_YEAR: {
      return DateTrunc.ISO_YEAR;
    }
    case DateGranularity.UNKNOWN:
    case undefined:
    default: {
      return DateTrunc.UNKNOWN;
    }
  }
};

function dateTruncFromSchema(schema?: IMetricDataPointsSchema) {
  return schema && schema.timestamp ? dateTruncFromGranularity(schema.timestamp.dateGranularity) : DateTrunc.UNKNOWN;
}
